import { useMemo, useState } from "react"
import { ArrowLeft, ArrowRight, Check, FileSignature } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useContracts } from "@/features/contracts/hooks/use-contracts"
import { useCreateOT } from "@/features/transit/hooks/use-transit-mutations"
import { StepContract } from "./step-contract"
import { StepLots } from "./step-lots"
import { paths } from "@/routes/paths"

interface NewOTDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  // When opened from a lot's detail page, the caller pre-seeds the
  // contract pick and the lot selection so the wizard skips re-asking
  // for state the user has already implied.
  initialContractId?: number | null
  initialLotIds?: number[]
}

type Step = 1 | 2

const STEPS: Step[] = [1, 2]

export function NewOTDialog({
  open,
  onOpenChange,
  initialContractId,
  initialLotIds,
}: NewOTDialogProps) {
  const { t } = useTranslation("transit")
  const navigate = useNavigate()
  const create = useCreateOT()

  const [step, setStep] = useState<Step>(1)
  const [contractId, setContractId] = useState<string | null>(
    initialContractId != null ? String(initialContractId) : null,
  )
  const [otNumber, setOtNumber] = useState("")
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(initialLotIds ?? []),
  )

  const { data: contracts } = useContracts()

  const contract = useMemo(
    () => contracts?.find((c) => String(c.id) === contractId) ?? null,
    [contracts, contractId],
  )

  const onPickContract = (id: string) => {
    setContractId(id)
  }

  // Reset wizard state whenever the dialog closes — driven by
  // onOpenChange instead of a useEffect. When the caller pre-seeded the
  // contract / lot selection, we restore those seeds instead of clearing.
  const handleOpenChange = (next: boolean) => {
    onOpenChange(next)
    if (!next) {
      setStep(1)
      setContractId(initialContractId != null ? String(initialContractId) : null)
      setOtNumber("")
      setSelected(new Set(initialLotIds ?? []))
    }
  }

  const step1Valid = !!contract
  const step2Valid = selected.size > 0

  const onToggleLot = (lotId: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(lotId)) next.delete(lotId)
      else next.add(lotId)
      return next
    })
  }

  const submit = async () => {
    if (!contract) return
    const trimmed = otNumber.trim()
    const ot = await create.mutateAsync({
      contractId: Number(contract.id),
      lotIds: Array.from(selected),
      otNumber: trimmed === "" ? undefined : trimmed,
    })
    handleOpenChange(false)
    navigate(paths.transitOrderDetail(ot.id))
  }

  const isLast = step === 2
  const isFirst = step === 1
  const canAdvance = (step === 1 && step1Valid) || step === 2

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("wizard.title")}</DialogTitle>
          <DialogDescription>
            {t("wizard.stepLabel", {
              current: step,
              total: STEPS.length,
              name: t(`wizard.step${step}Title`),
            })}
          </DialogDescription>
        </DialogHeader>

        <Stepper current={step} />

        <div className="py-2">
          {step === 1 ? (
            <StepContract
              contractId={contractId}
              onContractChange={onPickContract}
              otNumber={otNumber}
              onOtNumberChange={setOtNumber}
            />
          ) : null}
          {step === 2 ? (
            <StepLots
              contract={contract}
              selected={selected}
              onToggle={onToggleLot}
            />
          ) : null}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            disabled={create.isPending}
          >
            {t("wizard.cancel")}
          </Button>
          {!isFirst ? (
            <Button
              variant="outline"
              onClick={() => setStep((step - 1) as Step)}
              disabled={create.isPending}
            >
              <ArrowLeft className="size-4" />
              {t("wizard.back")}
            </Button>
          ) : null}
          {!isLast ? (
            <Button
              onClick={() => setStep((step + 1) as Step)}
              disabled={!canAdvance}
            >
              {t("wizard.next")}
              <ArrowRight className="size-4" />
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={!step1Valid || !step2Valid || create.isPending}
            >
              <FileSignature className="size-4" />
              {t("wizard.create")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Stepper({ current }: { current: Step }) {
  const { t } = useTranslation("transit")
  return (
    <ol className="flex items-center gap-2 text-xs">
      {STEPS.map((s, i) => {
        const done = s < current
        const active = s === current
        return (
          <li key={s} className="flex items-center gap-2">
            <span
              className={`flex size-6 items-center justify-center rounded-full text-[11px] font-medium tabular-nums ${
                active
                  ? "bg-primary text-primary-foreground"
                  : done
                    ? "bg-emerald-600 text-white"
                    : "bg-muted text-muted-foreground"
              }`}
              aria-current={active ? "step" : undefined}
            >
              {done ? <Check className="size-3" /> : s}
            </span>
            <span
              className={`hidden sm:inline ${
                active ? "text-foreground font-medium" : "text-muted-foreground"
              }`}
            >
              {t(`wizard.step${s}Title`)}
            </span>
            {i < STEPS.length - 1 ? (
              <span className="text-muted-foreground/40 mx-1">›</span>
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}
